import type { Listing, TargetPreview } from './data'

type LeaderboardPayload = { listings: Listing[]; online?: number; totalVisits?: number; minimumNextBidAed?: number }

type ApiBid = {
  id: string
  targetUrl: string
  targetHost: string
  amountAed: number
  clicks?: number
  clickUrl?: string
  currency: string
  status: string
  createdAt: string
  paidAt?: string | null
  title?: string | null
  description?: string | null
  imageUrl?: string | null
  iconUrl?: string | null
}

type ApiLeaderboard = {
  winner?: ApiBid | null
  leaderboard?: ApiBid[]
  minimumNextBidAed?: number
  currency?: string
}

const accents = ['#16835b', '#d14d42', '#1d5c8d', '#7355a6', '#d98b2b', '#181818', '#67824d', '#a35b3f']

function bidToListing(bid: ApiBid, index: number): Listing {
  const host = bid.targetHost || new URL(bid.targetUrl).hostname.replace(/^www\./, '')
  const xMatch = bid.targetUrl.match(/(?:x\.com|twitter\.com)\/([^/?#]+)/i)
  const name = bid.title || (xMatch ? `@${xMatch[1]}` : host)
  const handle = xMatch ? `@${xMatch[1]}` : undefined
  return {
    id: index + 1,
    name,
    handle,
    url: bid.clickUrl || bid.targetUrl,
    category: 'Live placement · UAE',
    description: bid.description || (xMatch ? 'X account' : `Visit ${host}`),
    bid: bid.amountAed,
    clicks: bid.clicks || 0,
    time: bid.paidAt ? 'live now' : 'recently',
    accent: accents[index % accents.length],
    initials: name.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || 'UAE',
    imageUrl: bid.imageUrl || undefined,
    iconUrl: bid.iconUrl || undefined,
  }
}

export async function fetchLeaderboard(): Promise<LeaderboardPayload> {
  try {
    const response = await fetch('/api/leaderboard', { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error('Leaderboard unavailable')
    const payload = (await response.json()) as LeaderboardPayload & ApiLeaderboard
    if (Array.isArray(payload.listings) && payload.listings.length > 0) return payload
    if (Array.isArray(payload.leaderboard) && payload.leaderboard.length > 0) {
      return { listings: payload.leaderboard.map(bidToListing), online: 0, totalVisits: 0, minimumNextBidAed: payload.minimumNextBidAed }
    }
    return { listings: [], online: 0, totalVisits: 0, minimumNextBidAed: payload.minimumNextBidAed || 5 }
  } catch {
    return { listings: [], online: 0, totalVisits: 0, minimumNextBidAed: 5 }
  }
}

export async function createCheckout(targetUrl: string, bidAmount: number, description?: string) {
  const response = await fetch('/api/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetUrl, bidAmount, description: description?.trim() || undefined }),
  })
  if (!response.ok) throw new Error('Checkout is not connected yet')
  return (await response.json()) as { checkoutUrl?: string; url?: string }
}

export async function fetchTargetPreview(target: string, signal?: AbortSignal): Promise<TargetPreview> {
  const response = await fetch(`/api/preview?target=${encodeURIComponent(target)}`, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) throw new Error('Preview unavailable')
  return (await response.json()) as TargetPreview
}
